/**
 * StereoRenderer — stereo 3D output for ordinary screens: 3D TVs and projectors (side-by-side,
 * top-and-bottom), full side-by-side pairs, and red/cyan anaglyph glasses.
 *
 * ── How the two eyes are drawn ──────────────────────────────────────────────
 * PlayCanvas 2.21 renders XR headsets through `Camera.xrViews`: a list of `RenderView`s, each
 * with its own projection, pose and viewport. The forward renderer draws every view of a camera
 * in one pass, and the unified gsplat pipeline has a native stereo path for it (one sort from
 * the camera position, both views projected — `gsplat-projector.js` `stereoMode`). We feed it
 * two RenderViews of our own, so the stereo pair costs ONE sort and ONE LOD world, exactly like
 * a headset. A second camera would instead get its own GSplatManager (sort, work buffer, LOD
 * streaming) per eye — see `gsplat-director.js`.
 *
 * The views are attached only while `app.render()` runs: set on 'prerender', cleared on
 * 'postrender'. Everywhere else the camera is an ordinary mono camera, so picking
 * (`pc.Picker.prepare` renders synchronously), `screenToWorld`, `worldToScreen` and reads of
 * `camera.fov` / `nearClip` all keep returning the mono values the rest of the viewer expects.
 *
 * ── Why an offscreen target ─────────────────────────────────────────────────
 * A 3D TV's half side-by-side frame is anamorphic: each eye is squeezed to half width. The
 * gsplat corner shader uses a single focal length for both axes, so a squeezed viewport would
 * flatten every splat vertically. Each eye is therefore rendered with square pixels into an
 * offscreen target, and a composite pass on a second camera (no layers, so no gsplat manager)
 * squeezes it onto the screen — or, for anaglyph, mixes the two eyes with Dubois matrices.
 *
 * ── Projection ──────────────────────────────────────────────────────────────
 * Off-axis (parallel axes, sheared frustums). See `computeEyeFrustum` in stereoMath.ts.
 */

import * as pc from 'playcanvas';
import {
    type StereoFormat,
    type StereoLayout,
    computeEyeFrustum,
    computeEyeSeparation,
    computeStereoLayout,
    clampStereoDepth,
    mapStereoPointerToMono,
} from './stereoMath';

export interface StereoRendererOptions {
    app: pc.AppBase;
    /** The viewer's main camera. Its parent (the rig) is respected, as in XR. */
    camera: pc.Entity;
    format: StereoFormat;
    /** 3D strength multiplier on the 1/30 rule (0..2, default 1). */
    depth?: number;
    /** Show the right eye's image where the left eye's belongs (cross-eyed viewing, flipped glasses). */
    swapEyes?: boolean;
    /**
     * Distance from the camera, in scene units, that appears at the screen surface. Called every
     * frame, so it can follow an orbit focus.
     */
    getScreenDistance: () => number;
}

export interface StereoRendererHandle {
    readonly format: StereoFormat;
    setFormat(format: StereoFormat): void;
    setDepth(depth: number): void;
    setSwapEyes(swap: boolean): void;
    /** Map a canvas pointer position (CSS px) to the mono frame position it looks at. */
    mapPointer(x: number, y: number, width: number, height: number): { x: number; y: number };
    /** Restores the camera and frees the target. Idempotent. */
    destroy(): void;
}

/**
 * Dubois least-squares red/cyan matrices (rows: output R, G, B; columns: input R, G, B), applied
 * in linear light. They cut the ghosting and retinal rivalry of the naive "red from left,
 * green+blue from right" split while keeping most of the colour.
 */
const ANAGLYPH_GLSL = /* glsl */ `
uniform sampler2D stereoTexture;
uniform float stereoDecodeGamma;
varying vec2 uv0;

vec3 stereoToLinear(vec3 c) { return mix(c, pow(c, vec3(2.2)), stereoDecodeGamma); }
vec3 stereoToOutput(vec3 c) { return mix(c, pow(c, vec3(1.0 / 2.2)), stereoDecodeGamma); }

void main(void) {
    vec3 l = stereoToLinear(texture2D(stereoTexture, vec2(uv0.x * 0.5, uv0.y)).rgb);
    vec3 r = stereoToLinear(texture2D(stereoTexture, vec2(0.5 + uv0.x * 0.5, uv0.y)).rgb);
    vec3 c = vec3(
        dot(vec3( 0.456,  0.500,  0.176), l) + dot(vec3(-0.043, -0.088, -0.002), r),
        dot(vec3(-0.040, -0.038, -0.016), l) + dot(vec3( 0.378,  0.734, -0.018), r),
        dot(vec3(-0.015, -0.021, -0.005), l) + dot(vec3(-0.072, -0.113,  1.226), r)
    );
    gl_FragColor = vec4(stereoToOutput(clamp(c, 0.0, 1.0)), 1.0);
}
`;

const ANAGLYPH_WGSL = /* wgsl */ `
var stereoTexture: texture_2d<f32>;
var stereoTextureSampler: sampler;
uniform stereoDecodeGamma: f32;
varying uv0: vec2f;

fn stereoToLinear(c: vec3f) -> vec3f { return mix(c, pow(c, vec3f(2.2)), uniform.stereoDecodeGamma); }
fn stereoToOutput(c: vec3f) -> vec3f { return mix(c, pow(c, vec3f(1.0 / 2.2)), uniform.stereoDecodeGamma); }

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let l = stereoToLinear(textureSample(stereoTexture, stereoTextureSampler, vec2f(input.uv0.x * 0.5, input.uv0.y)).rgb);
    let r = stereoToLinear(textureSample(stereoTexture, stereoTextureSampler, vec2f(0.5 + input.uv0.x * 0.5, input.uv0.y)).rgb);
    let c = vec3f(
        dot(vec3f( 0.456,  0.500,  0.176), l) + dot(vec3f(-0.043, -0.088, -0.002), r),
        dot(vec3f(-0.040, -0.038, -0.016), l) + dot(vec3f( 0.378,  0.734, -0.018), r),
        dot(vec3f(-0.015, -0.021, -0.005), l) + dot(vec3f(-0.072, -0.113,  1.226), r)
    );
    output.color = vec4f(stereoToOutput(clamp(c, vec3f(0.0), vec3f(1.0))), 1.0);
    return output;
}
`;

/** Side-by-side / top-and-bottom: the target already holds the final layout; stretch-copy it. */
const COPY_GLSL = /* glsl */ `
uniform sampler2D stereoTexture;
varying vec2 uv0;
void main(void) {
    gl_FragColor = vec4(texture2D(stereoTexture, uv0).rgb, 1.0);
}
`;

const COPY_WGSL = /* wgsl */ `
var stereoTexture: texture_2d<f32>;
var stereoTextureSampler: sampler;
varying uv0: vec2f;
@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    output.color = vec4f(textureSample(stereoTexture, stereoTextureSampler, input.uv0).rgb, 1.0);
    return output;
}
`;

class StereoComposePass extends pc.RenderPassShaderQuad {
    source: pc.Texture | null = null;
    decodeGamma = 0;
    private readonly sourceId: pc.ScopeId;
    private readonly decodeGammaId: pc.ScopeId;

    constructor(device: pc.GraphicsDevice) {
        super(device);
        this.sourceId = device.scope.resolve('stereoTexture');
        this.decodeGammaId = device.scope.resolve('stereoDecodeGamma');
    }

    setAnaglyph(anaglyph: boolean): void {
        this.shader = pc.ShaderUtils.createShader(this.device, {
            uniqueName: anaglyph ? 'StorySplatStereoAnaglyph' : 'StorySplatStereoCopy',
            attributes: { aPosition: pc.SEMANTIC_POSITION },
            vertexChunk: 'quadVS',
            fragmentGLSL: anaglyph ? ANAGLYPH_GLSL : COPY_GLSL,
            fragmentWGSL: anaglyph ? ANAGLYPH_WGSL : COPY_WGSL,
        });
    }

    execute(): void {
        if (!this.source) return;
        this.sourceId.setValue(this.source);
        this.decodeGammaId.setValue(this.decodeGamma);
        super.execute();
    }
}

type XrProperties = { horizontalFov: boolean; fov: number; aspectRatio: number; farClip: number; nearClip: number };
type CameraInternals = pc.Camera & { _xrProperties: XrProperties };

/**
 * The offscreen target keeps the backbuffer's colour encoding, so the copy pass is an exact
 * pass-through: an sRGB backbuffer gets an sRGB target (camera writes linear, hardware encodes
 * and decodes), an HDR one keeps half floats, everything else is plain RGBA8 holding the
 * camera's gamma-corrected output.
 */
function pickTargetFormat(device: pc.GraphicsDevice): number {
    const backBufferFormat: number | undefined = device.backBufferFormat;
    if (backBufferFormat !== undefined && pc.isSrgbPixelFormat(backBufferFormat)) return pc.PIXELFORMAT_SRGBA8;
    if (backBufferFormat === pc.PIXELFORMAT_RGBA16F) return pc.PIXELFORMAT_RGBA16F;
    return pc.PIXELFORMAT_RGBA8;
}

const _camPos = new pc.Vec3();
const _camRight = new pc.Vec3();
const _eyePos = new pc.Vec3();
const _eyeWorld = new pc.Mat4();
const _eyeLocal = new pc.Mat4();
const _parentInv = new pc.Mat4();
const _proj = new pc.Mat4();
const _shaderProj = new pc.Mat4();

export function createStereoRenderer(options: StereoRendererOptions): StereoRendererHandle {
    const { app, camera, getScreenDistance } = options;
    const cameraComponent = camera.camera;
    if (!cameraComponent) throw new Error('[StereoRenderer] camera entity has no camera component');
    const cam = cameraComponent.camera as CameraInternals;
    const device = app.graphicsDevice;

    let format = options.format;
    let depth = clampStereoDepth(options.depth);
    let swapEyes = options.swapEyes === true;
    let destroyed = false;

    // ── Camera state we take over, restored on destroy ──
    const prevRenderTarget = cameraComponent.renderTarget;
    const prevAspectMode = cameraComponent.aspectRatioMode;
    const prevAspect = cameraComponent.aspectRatio;
    const prevXrProperties = { ...cam._xrProperties };
    // The legacy post-effect queue (the viewer's FXAA) points camera.renderTarget at its own
    // buffer and draws the effect from onPostprocessing; it can't wrap the eye pair. Its
    // disable()/enable() is not a round trip (disable destroys the input target), so park the
    // hook instead and hand both back on destroy.
    const prevOnPostprocessing = cameraComponent.onPostprocessing;
    cameraComponent.onPostprocessing = null;

    // ── Offscreen target ──
    const targetFormat = pickTargetFormat(device);
    const targetIsSrgb = pc.isSrgbPixelFormat(targetFormat);
    let layout: StereoLayout = computeStereoLayout(format, device.width, device.height, device.maxTextureSize);
    const colorBuffer = new pc.Texture(device, {
        name: 'storysplat-stereo-eyes',
        width: layout.targetWidth,
        height: layout.targetHeight,
        format: targetFormat,
        mipmaps: false,
        minFilter: pc.FILTER_LINEAR,
        magFilter: pc.FILTER_LINEAR,
        addressU: pc.ADDRESS_CLAMP_TO_EDGE,
        addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });
    const renderTarget = new pc.RenderTarget({
        name: 'storysplat-stereo-eyes',
        colorBuffer,
        depth: true,
    });
    cameraComponent.renderTarget = renderTarget;

    // ── Composite camera: draws the target to the screen after the main camera ──
    const composePass = new StereoComposePass(device);
    composePass.init(null);
    // Black outside the drawn region (full side-by-side is letterboxed).
    composePass.setClearColor(pc.Color.BLACK);
    const composeViewport = new pc.Vec4();
    composePass.viewport = composeViewport;
    composePass.source = colorBuffer;
    composePass.setAnaglyph(format === 'anaglyph');
    // The layer composition only finds cameras through their layers, so the composite camera
    // gets one of its own. It stays empty: GSplatDirector creates a sort + work buffer per
    // (camera, layer) pair that holds splats, and this camera only runs its frame pass.
    const composeLayer = new pc.Layer({ name: 'StorySplatStereoComposite' });
    app.scene.layers.push(composeLayer);
    const composeEntity = new pc.Entity('storysplat-stereo-composite');
    composeEntity.addComponent('camera', {
        layers: [composeLayer.id],
        priority: cameraComponent.priority + 1000,
        clearColorBuffer: false,
        clearDepthBuffer: false,
        clearStencilBuffer: false,
    });
    composeEntity.camera!.framePasses = [composePass];
    app.root.addChild(composeEntity);

    const views: [pc.RenderView, pc.RenderView] = [new pc.RenderView(), new pc.RenderView()];

    const syncDecodeGamma = () => {
        // RGBA8 targets hold gamma-encoded colour; the anaglyph mix must run in linear light.
        composePass.decodeGamma = !targetIsSrgb && cameraComponent.gammaCorrection !== pc.GAMMA_NONE ? 1 : 0;
    };
    syncDecodeGamma();

    const onPrerender = () => {
        if (destroyed) return;
        // A headset session owns xrViews; never overwrite them (the viewer also stops stereo
        // before entering VR/AR, this is only a guard).
        if (app.xr?.active) return;

        const width = device.width;
        const height = device.height;
        if (width < 1 || height < 1) return;

        // Mono aspect: with a render target set, AUTO would use the target's size.
        const monoAspect = width / height;
        if (cameraComponent.aspectRatioMode !== pc.ASPECT_MANUAL) cameraComponent.aspectRatioMode = pc.ASPECT_MANUAL;
        if (cameraComponent.aspectRatio !== monoAspect) cameraComponent.aspectRatio = monoAspect;

        const next = computeStereoLayout(format, width, height, device.maxTextureSize);
        if (next.targetWidth !== renderTarget.width || next.targetHeight !== renderTarget.height) {
            renderTarget.resize(next.targetWidth, next.targetHeight);
        }
        layout = next;
        composeViewport.set(
            Math.round(layout.display.x * width),
            Math.round(layout.display.y * height),
            Math.round(layout.display.width * width),
            Math.round(layout.display.height * height),
        );

        // Mono parameters, read while xrViews is still null (the getters switch once it is set).
        const fov = cameraComponent.fov;
        const horizontalFov = cameraComponent.horizontalFov;
        const near = cameraComponent.nearClip;
        const far = cameraComponent.farClip;
        const screenDistance = Math.max(near * 2, getScreenDistance() || 0);
        const separation = computeEyeSeparation(screenDistance, depth);

        _camPos.copy(camera.getPosition());
        const rotation = camera.getRotation();
        rotation.transformVector(pc.Vec3.RIGHT, _camRight);
        const parent = camera.parent as pc.GraphNode | null;
        if (parent) _parentInv.copy(parent.getWorldTransform()).invert();

        syncDecodeGamma();
        const webgpu = device.isWebGPU;
        const flipY = !!renderTarget.flipY;
        for (let slot = 0; slot < 2; slot++) {
            // Slot 0 is the left-eye region of the layout; swapping puts the right eye there.
            const eyeSign = (slot === 0) !== swapEyes ? -1 : 1;
            const eyeOffset = (eyeSign * separation) / 2;
            const f = computeEyeFrustum(fov, horizontalFov, layout.eyeAspect, near, eyeOffset, screenDistance);
            _proj.setFrustum(f.left, f.right, f.bottom, f.top, near, far);
            // The XR branch of the renderer uses view.projMat as-is, so apply the same clip-space
            // fix-ups (WebGPU depth range, flipped targets) the mono branch applies itself.
            pc.Camera.applyShaderProjectionTransform(_proj, _shaderProj, flipY, webgpu);

            _eyePos.copy(_camRight).mulScalar(eyeOffset).add(_camPos);
            _eyeWorld.setTRS(_eyePos, rotation, pc.Vec3.ONE);
            // RenderView poses are relative to the camera's parent (the renderer multiplies the
            // parent's world transform back in), matching how XR rigs work.
            if (parent) _eyeLocal.mul2(_parentInv, _eyeWorld);
            else _eyeLocal.copy(_eyeWorld);

            const view = views[slot];
            view.setView(_shaderProj.data, _eyeLocal.data);
            const vp = layout.viewports[slot];
            view.setViewport(vp.x, vp.y, vp.width, vp.height);
        }

        // While xrViews is set, Camera's fov/clip/aspect getters read _xrProperties instead.
        const xp = cam._xrProperties;
        xp.fov = fov;
        xp.horizontalFov = horizontalFov;
        xp.nearClip = near;
        xp.farClip = far;
        xp.aspectRatio = monoAspect;

        cam.xrViews = views;
    };

    const onPostrender = () => {
        if (cam.xrViews === views) cam.xrViews = null;
    };

    app.on('prerender', onPrerender);
    app.on('postrender', onPostrender);
    app.renderNextFrame = true;

    return {
        get format() {
            return format;
        },
        setFormat(next: StereoFormat) {
            if (destroyed || next === format) return;
            format = next;
            composePass.setAnaglyph(format === 'anaglyph');
            app.renderNextFrame = true;
        },
        setDepth(next: number) {
            depth = clampStereoDepth(next);
            app.renderNextFrame = true;
        },
        setSwapEyes(next: boolean) {
            swapEyes = next === true;
            app.renderNextFrame = true;
        },
        mapPointer(x: number, y: number, width: number, height: number) {
            return mapStereoPointerToMono(format, x, y, width, height);
        },
        destroy() {
            if (destroyed) return;
            destroyed = true;
            app.off('prerender', onPrerender);
            app.off('postrender', onPostrender);
            if (cam.xrViews === views) cam.xrViews = null;
            Object.assign(cam._xrProperties, prevXrProperties);

            composeEntity.camera!.framePasses = [];
            composeEntity.destroy();
            composePass.destroy();
            app.scene.layers.remove(composeLayer);

            if (cameraComponent.renderTarget === renderTarget) cameraComponent.renderTarget = prevRenderTarget;
            cameraComponent.aspectRatioMode = prevAspectMode;
            if (prevAspectMode === pc.ASPECT_MANUAL) cameraComponent.aspectRatio = prevAspect;
            if (cameraComponent.onPostprocessing === null) cameraComponent.onPostprocessing = prevOnPostprocessing;

            renderTarget.destroyTextureBuffers();
            renderTarget.destroy();
            app.renderNextFrame = true;
        },
    };
}
